import { Typography } from "@mui/material";
import { createStyles, makeStyles } from "@mui/styles";
import { useState } from "react";
import { Box } from "@mui/system";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import { FileUpload } from "../../redux/actions/FileUpload/FileUpload";
import { useDispatch } from "react-redux";
import { triggerAppAlert } from './../../redux/actions/SelfServicePortal/ContactAid/ContactAid';
import { fileUploadServiceNew } from "../../services/fileUpload/FileUpload";
import SmartDisplayIcon from "@mui/icons-material/SmartDisplay";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DescriptionIcon from "@mui/icons-material/Description";
import FolderZipIcon from "@mui/icons-material/FolderZip";
import TableChartIcon from "@mui/icons-material/TableChart";
import TextSnippetIcon from "@mui/icons-material/TextSnippet";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import { v4 as uuidv4 } from 'uuid';

function base64ToBlob(base64: any, contentType = '', sliceSize = 512) {
  // Check if the base64 string has a data URL prefix
  if (base64.includes(',')) {
      // Split the base64 string to remove the prefix
      base64 = base64.split(',')[1];
  }

  const byteCharacters = atob(base64);
  const byteArrays = [];

  for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
      const slice = byteCharacters.slice(offset, offset + sliceSize);
      const byteNumbers = new Array(slice.length);

      for (let i = 0; i < slice.length; i++) {
          byteNumbers[i] = slice.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);
      byteArrays.push(byteArray);
  }

  return new Blob(byteArrays, { type: contentType });
}


export function getFileNameAndExtension(url: string) {
  try {
    // Extract file name from URL
    const filenameRegex = /\/([^\/?#]+)$/;
    const filenameMatches = filenameRegex.exec(url);
    let filename = filenameMatches ? filenameMatches[1] : null;

    // Strip UUID prefix from filename (format: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx-originalname.ext)
    // UUID regex: 8 chars - 4 chars - 4 chars - 4 chars - 12 chars followed by dash
    if (filename) {
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}-(.+)$/i;
      const match = uuidRegex.exec(filename);
      if (match) {
        filename = match[1]; // Extract original filename without UUID prefix
      }
    }

    // Extract file extension from URL
    const extensionRegex = /\.([0-9a-z]+)(?:[\?#]|$)/i;
    const extensionMatches = extensionRegex.exec(url);
    const fileExtension = extensionMatches ? extensionMatches[1] : null;

    // Determine MIME type based on file extension
    let type = "unknown";
    if (fileExtension) {
      switch (fileExtension.toLowerCase()) {
        case "png":
        case "jpg":
        case "jpeg":
        case "bmp":
          type = "image";
          break;
        case "pdf":
          type = "pdf";
          break;
        case "doc":
        case "docx":
          type = "doc";
          break;
        case "xls":
        case "xlsx":
          type = "excel";
          break;
        case "zip":
          type = "archive";
          break;
        case "txt":
          type = "text";
          break;
        case "mp4":
          type = "video";
          break;
      }
    }

    return { filename, fileExtension, type };
  } catch (error) {
    console.error("Error:", error);
    return { filename: null, fileExtension: null, type: "unknown" };
  }
}

const useStyles = makeStyles(() =>
  createStyles({
    attachmentBox: {
      border: "1px solid #ddd",
      display: "flex",
      flexDirection: "row",
      gap: 10,
      padding: 10,
      alignItems: "center",
      "& img": {
        height: "30px",
      },
      "& p": {
        fontSize: 10,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        maxWidth: 70,
      },
      "& svg": {
        height: "20px !important",
        width: "20px !important",
        color: "#ff0000",
      },
    },
  })
);

const addAttchmentUsingFileAPI = async (fileObject: any) => {
//   console.log("addAttchmentfileObject", fileObject);
  let res: any = await fileUploadServiceNew(fileObject);
  return res;
};

// Helper function to render file preview/icon based on type
const renderFilePreview = (attachment: any, onClick?: () => void) => {
  const fileInfo = attachment.preview
    ? { fileType: attachment.fileType, type: getTypeFromMime(attachment.fileType) }
    : getFileNameAndExtension(attachment.attachmentPath);

  const iconStyle = {
    width: "40px",
    height: "40px",
    color: "#666",
  };

  // For images, show actual preview
  if (fileInfo.type === "image" || attachment.fileType?.startsWith("image/")) {
    return (
      <img
        src={attachment.preview || attachment.attachmentPath}
        alt="Preview"
        style={{ width: "40px", height: "40px", objectFit: "cover", borderRadius: "4px" }}
        onError={(e) => {
          // Fallback to icon if image fails to load
          e.currentTarget.style.display = 'none';
        }}
      />
    );
  }

  // For videos
  if (fileInfo.type === "video" || attachment.fileType === "video/mp4") {
    return <SmartDisplayIcon sx={iconStyle} onClick={onClick} style={{ cursor: onClick ? "pointer" : "default" }} />;
  }

  // For PDFs
  if (fileInfo.type === "pdf" || attachment.fileType === "application/pdf") {
    return <PictureAsPdfIcon sx={{ ...iconStyle, color: "#d32f2f" }} />;
  }

  // For Word documents
  if (fileInfo.type === "doc" || attachment.fileType?.includes("word") || attachment.fileType?.includes("document")) {
    return <DescriptionIcon sx={{ ...iconStyle, color: "#2196f3" }} />;
  }

  // For Excel files
  if (fileInfo.type === "excel" || attachment.fileType?.includes("spreadsheet") || attachment.fileType?.includes("excel")) {
    return <TableChartIcon sx={{ ...iconStyle, color: "#4caf50" }} />;
  }

  // For ZIP files
  if (fileInfo.type === "archive" || attachment.fileType?.includes("zip") || attachment.fileType?.includes("compressed")) {
    return <FolderZipIcon sx={{ ...iconStyle, color: "#ff9800" }} />;
  }

  // For text files
  if (fileInfo.type === "text" || attachment.fileType === "text/plain") {
    return <TextSnippetIcon sx={{ ...iconStyle, color: "#9e9e9e" }} />;
  }

  // Default file icon
  return <InsertDriveFileIcon sx={iconStyle} />;
};

// Helper to get type from MIME type
const getTypeFromMime = (mimeType: string) => {
  if (!mimeType) return "unknown";
  if (mimeType.startsWith("image/")) return "image";
  if (mimeType.startsWith("video/")) return "video";
  if (mimeType.includes("pdf")) return "pdf";
  if (mimeType.includes("word") || mimeType.includes("document")) return "doc";
  if (mimeType.includes("spreadsheet") || mimeType.includes("excel")) return "excel";
  if (mimeType.includes("zip") || mimeType.includes("compressed")) return "archive";
  if (mimeType.includes("text")) return "text";
  return "unknown";
};

const AddAttachmentBlobWithPreview = (props: any) => {
  
  const dispatch = useDispatch();
  const [fName,setfName] = useState<string[]>([])
  const classes = useStyles();

  const imgCallBack = async (files: any) => {
//     console.log("AddAttachmentBlobWithPreview", files, files.length > 0);
    if (files.length > 0) {
      const filesArray = Array.from(files);

      if (props.isAttachmentOnFly) {
        // Upload multiple files and wait for all to complete
        const uploadPromises = filesArray.map((file: any) => {
//           console.log("imgCallBack files", file);
          const fileName = `${uuidv4()}-${file?.name}`;

          let fileObject = {
            name: fileName,
            fileTypeId: 1,
            ExtensionTypeId: 1,
            formFile: file,
            moduleType: "quicksendmailattachment",
          };
          setfName(prev => [...prev, file.name]);

          if(fName.includes(file.name)){
            setfName(prev => prev.slice(0, -1));
             dispatch(triggerAppAlert(`You’ve already uploaded this file : ${file.name}`, "warning"));
             return false
          }
          return addAttchmentUsingFileAPI(fileObject).then((res: any) => {
            if (res && res.data) {
//               console.log("addAttchmentUsingFileAPI res", res.data.filePath);
              return {
                attachmentPath: res.data.filePath,
                attachmentId: res.data.id,
                fileName: fileName,
                fileType: file?.type,
              };
            } else {
              console.error("File upload failed for:", file.name, res);
              alert(`Failed to upload file: ${file.name}. Please try again.`);
              return null;
            }
          }).catch((error: any) => {
            console.error("File upload error for:", file.name, error);
            alert(`Error uploading file: ${file.name}. ${error.message || 'Please try again.'}`);
            return null;
          });
         
        });

        // Wait for all uploads to complete
        const uploadedFiles = await Promise.all(uploadPromises);

        // Filter out any failed uploads and update state once
        const successfulUploads = uploadedFiles.filter(file => file !== null && file !== false  && file !== undefined);
        const failedCount = uploadedFiles.length - successfulUploads.length;
        
        if (successfulUploads.length > 0) {
          props.setAttachmentList([...props?.attachmentList, ...successfulUploads]);
        
        }

        if (failedCount > 0) {
          // alert(`${failedCount} file(s) failed to upload. Please check console for details.`);
        }
      } else {
        // Process files for preview (non-fly mode)
        const readPromises = filesArray.map((file: any) => {
          return new Promise((resolve) => {
            const reader = new FileReader();
            const fileName = `${uuidv4()}-${file?.name}`;

            reader.onloadend = () => {
              resolve({
                preview: reader.result,
                fileName: fileName,
                fileType: file?.type,
                formFile: file,
                type: props?.type
              });
            };
            reader.readAsDataURL(file);
          });
        });

        // Wait for all files to be read
        const readFiles = await Promise.all(readPromises);
        props.setAttachmentList([...props?.attachmentList, ...readFiles]);
      }
    }
  };

  const handleDelete = (index: any) => {
    setfName(prev => prev.filter((_, i) => i !== index));
    props.setAttachmentList(
      props.attachmentList.filter((_: any, i: any) => i !== index)
    );
  };

  return (
    <>
      {props.isShowBtn && (
        <Box>
          <>
            {" "}
            <input
              id={props.type}
              accept={props?.accept}
              type="file"
              multiple={true}
              onChange={(e: any) => {
                imgCallBack(e.target.files);
                e.target.value = "";
              }}
              //value={FileUploadVal}
              className="d-none"
            />
            <label
              className="Add-Attachment"
              htmlFor={props.type}
              style={{
                fontSize: "16px",
                cursor: "pointer",
                color: "#0A93FC",
                display: "inline-block",
                padding: "10px 0",
              }}
            >
              {props?.btnLabel}{" "}
              {"("}
              {props?.attachmentList?.length}
              {")"}
            </label>
          </>
        </Box>
      )}

      {props?.attachmentList?.length > 0 && (
        <div
          style={{
            marginLeft: "0px",
            display: "flex",
            flexDirection: "row",
            gap: 10,
            flexWrap: "wrap",
          }}
        >
          {props?.attachmentList?.map((i: any, index: number) => {
            return (
              <>
                <Box className="col-auto">
                  <Box
                    className={`${classes.attachmentBox}`}
                    onClick={() => {
                      
                    }}
                  >
                    {renderFilePreview(i, () => {
                      // Handle preview click for videos
                      if (i?.fileType === "video/mp4" && i?.preview) {
//                         console.log("preview check", i?.preview);
                        const base64String = i?.preview;
                        const mimeType = "video/mp4";
                        const blob = base64ToBlob(base64String, mimeType);
                        const url = URL.createObjectURL(blob);
                        window.open(url, "_blank")?.focus();
                      }
                    })}
                    
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      <Typography variant="body2">
                        {i?.preview
                          ? i?.fileName?.length > 7
                            ? i?.fileName?.slice(0, 7)
                            : i?.fileName
                          : getFileNameAndExtension(i?.attachmentPath)
                              ?.filename}
                      </Typography>
                      <Typography style={{ color: "gray" }} variant="body2">
                        {i?.preview
                          ? i?.fileType
                          : getFileNameAndExtension(i?.attachmentPath)
                              ?.fileExtension}
                      </Typography>
                    </div>
                    <Box className="cursor-pointer">
                      <DeleteOutlinedIcon
                        className="cursor-pointer"
                        onClick={() => {
                          handleDelete(index);
                        }}
                      />
                    </Box>
                  </Box>
                </Box>
              </>
            );
          })}
        </div>
      )}
    </>
  );
};

export default AddAttachmentBlobWithPreview;
